import React, { useState } from 'react';
import { Box, FormControlLabel, Switch, Typography, useTheme } from '@mui/material';
import BackdropDialog from 'common/components/BackdropDialog';
import { RiExternalLinkLine } from 'react-icons/ri';
import { useTranslation } from 'services/localization';

type ProductCreatedModalProps = {
	open: boolean;
	hasPaymentMethods: boolean;
	onClose: (enablePayInStore: boolean) => void;
	onConfirm: () => void;
};

const ProductCreatedModal = (props: ProductCreatedModalProps) => {
	const { open, hasPaymentMethods, onClose, onConfirm } = props;
	const [enablePayInStore, setEnablePayInStore] = useState(false);
	const theme = useTheme();
	const { t } = useTranslation();

	return (
		<BackdropDialog
			isOpen={open}
			onClose={() => onClose(enablePayInStore)}
			variant="primary"
			width="sm"
			label={`${'🎉 ' + t('homeView.productCreatedModal.productCreated', 'Product created') + '!'}`}
			body={
				<>
					<Typography variant="body2">
						{t(
							'homeView.productCreatedModal.firstProductCreated',
							'Good job creating your first product! Next, you should try booking it in your Online Store!',
						)}
					</Typography>
					<Box mb={2} />
					<Typography variant="body2">
						{t(
							'homeView.productCreatedModal.exceptionFirstProductVisible',
							`By default, products are created as hidden, so you can finish setting them up before publishing them in your Online Store. For your first product, we've made an exception and automatically published it for you to test.`,
						)}
					</Typography>
					<Box mb={2} />
					{!hasPaymentMethods && (
						<>
							<Typography variant="body2">
								{t(
									'homeView.productCreatedModal.onePaymentMethodNeeded',
									'To create an order in your Online Store, you need at least one payment method enabled.',
								)}
							</Typography>
							<Box mb={4} />
							<FormControlLabel
								sx={{
									label: {
										fontSize: 14,
										fontWeight: 'bold',
									},
								}}
								control={
									<Switch
										color="primary"
										checked={enablePayInStore}
										onChange={() => setEnablePayInStore(!enablePayInStore)}
										value="enablePayOnPickup"
									/>
								}
								label={t(
									'homeView.productCreatedModal.enablePayInStore',
									'Enable payment method "Pay in-store"',
								)}
							/>
						</>
					)}
				</>
			}
			confirm={{
				label: t('homeView.productCreatedModal.tryMyOnlineStore', 'Try my online store'),
				icon: {
					icon: <RiExternalLinkLine size={24} />,
					position: 'right',
				},
				callback: onConfirm,
				closeAfterDone: true,
				sx: {
					backgroundColor: theme.palette.primary.main,
					'&:hover': {
						backgroundColor: theme.palette.primary.dark,
					},
				},
				disabled: !hasPaymentMethods && !enablePayInStore,
			}}
			cancel={{
				label: t('common:actions.close'),
				autoFocus: false,
				callback: () => onClose(enablePayInStore),
			}}
		/>
	);
};

export default ProductCreatedModal;
